import graphqlClientFactory from 'lib/graphql-client-factory';
import { ImportedData, ImportedDataType } from './ImportedData.types';
import { sparkleUtils } from 'src/shared/sparkle-utils';

type GraphQLResults = {
  search: {
    results: Array<{
      [key: string]: {
        value: string;
      };
    }>;
  };
};

export class ImportedDataService {
  private _maxRetries: number;
  constructor(maxRetries: number) {
    this._maxRetries = maxRetries;
  }

  public async getData(slug: string, dataType: ImportedDataType): Promise<ImportedData> {
    if (sparkleUtils.isEmptyOrWhiteSpace(slug)) return {} as ImportedData;

    const query = ImportedDataService.getQueryFor(dataType);
    if (!query) return {} as ImportedData;

    const graphQLClient = graphqlClientFactory({
      retries: this._maxRetries,
    });

    try {
      const result = await graphQLClient.request<GraphQLResults>(query, {
        parentItemId: ImportedDataService.getIdFor(dataType),
        slug: slug,
      });
      if (result?.search?.results?.length === 1) {
        return ImportedDataService.unpackGraphQLResults(result);
      } else {
        throw new Error('No result found.');
      }
    } catch (error) {
      return {} as ImportedData;
    }
  }

  private static unpackGraphQLResults(results: GraphQLResults): ImportedData {
    const data: { [key: string]: string } = {};

    const rawObj = results.search.results[0];
    for (const key in rawObj) {
      if (rawObj.hasOwnProperty(key) && rawObj[key]) {
        data[key] = rawObj[key].value;
      }
    }

    return data as unknown as ImportedData;
  }

  private static getIdFor(dataType: ImportedDataType): string {
    switch (dataType) {
      case ImportedDataType.SparkleEvent:
        return 'FF1D1F2E-77C8-449F-B43D-F59408B585FB';
      case ImportedDataType.SparkleLocation:
        return 'FE0C7CEF-7BE7-4E20-891D-25EAB47E4839';
      case ImportedDataType.SparkleProvider:
        return '7AD89CAF-5053-4D3F-866E-665C17AF4996';
      default:
        return '';
    }
  }

  private static getQueryFor(dataType: ImportedDataType): string {
    switch (dataType) {
      case ImportedDataType.SparkleProvider:
        return `query ImportedSparkleProviderItem (
          $parentItemId: String
          $slug: String
        ){
          search(
            where:{
              AND: [
                {
                  name: "_path",
                  value: $parentItemId,
                  operator: CONTAINS
                }
                {
                  name: "_name",
                  value: $slug,
                  operator: EQ
                }
              ]
            }
            first: 1
          ) {
            results {
              browserTitle: field(name: "BrowserTitle") { value }
              canonicalUrl: field(name: "CanonicalUrl") { value }
              pageKeywords: field(name: "MetaKeywords") { value }
              pageDescription: field(name: "MetaDescription") { value }
              pageType: field(name: "OpenGraphType") { value }
              imageUrl: field(name: "ImageUrl") { value }
              imageWidth: field(name: "ImageWidth") { value }
              imageHeight: field(name: "ImageHeight") { value }
              imageType: field(name: "ImageType") { value }
              firstName: field(name: "FirstName") { value }
              lastName: field(name: "LastName") { value }
              gender: field(name: "Gender") { value }
              jsonPayload: field(name: "JSONPayload") { value }
              navigationTitle: field(name: "NavigationTitle") { value }
              siteName: field(name: "OpenGraphSiteName") { value }
            }
          }
        }`;
      case ImportedDataType.SparkleLocation:
        return `query ImportedSparkleLocationItem (
          $parentItemId: String
          $slug: String
        ){
          search(
            where:{
              AND: [
                {
                  name: "_path",
                  value: $parentItemId,
                  operator: CONTAINS
                }
                {
                  name: "_name",
                  value: $slug,
                  operator: EQ
                }
              ]
            }
            first: 1
          ) {
            results {
              browserTitle: field(name: "BrowserTitle") { value }
              canonicalUrl: field(name: "CanonicalUrl") { value }
              pageKeywords: field(name: "MetaKeywords") { value }
              pageDescription: field(name: "MetaDescription") { value }
              pageType: field(name: "OpenGraphType") { value }
              imageUrl: field(name: "ImageUrl") { value }
              imageWidth: field(name: "ImageWidth") { value }
              imageHeight: field(name: "ImageHeight") { value }
              imageType: field(name: "ImageType") { value }
              publishedTime: field(name: "PublishedTime") { value }
              modifiedTime: field(name: "ModifiedTime") { value }
              section: field(name: "Section") { value }
              jsonPayload: field(name: "JSONPayload") { value }
              navigationTitle: field(name: "NavigationTitle") { value }
              siteName: field(name: "OpenGraphSiteName") { value }
            }
          }
        }`;
      case ImportedDataType.SparkleEvent:
        return `query ImportedSparkleEventItem (
            $parentItemId: String
            $slug: String
          ){
            search(
              where:{
                AND: [
                  {
                    name: "_path",
                    value: $parentItemId,
                    operator: CONTAINS
                  }
                  {
                    name: "_name",
                    value: $slug,
                    operator: EQ
                  }
                ]
              }
              first: 1
            ) {
              results {
                browserTitle: field(name: "BrowserTitle") { value }
                canonicalUrl: field(name: "CanonicalUrl") { value }
                pageKeywords: field(name: "MetaKeywords") { value }
                pageDescription: field(name: "MetaDescription") { value }
                pageType: field(name: "OpenGraphType") { value }
                publishedTime: field(name: "PublishedTime") { value }
                modifiedTime: field(name: "ModifiedTime") { value }
                section: field(name: "Section") { value }
                jsonPayload: field(name: "JSONPayload") { value }
                navigationTitle: field(name: "NavigationTitle") { value }
                siteName: field(name: "OpenGraphSiteName") { value }
              }
            }
          }`;
      default:
        return '';
    }
  }
}
